import { useCallback, useEffect, useState } from "react";
import { useIsPropertiesPanelRevampEnabled } from "@circle-react-shared/BuilderUI/Hooks/useIsPropertiesPanelRevamp";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { Inputs } from "./Inputs";
import { Selector } from "./Selector";
import type { ButtonRadiusSelectorType } from "./constants";
import { CORNERS } from "./constants";
import { AllValuesInputField } from "./AllValuesInputField";

interface BorderRadiusValues {
  border_left_bottom_radius?: string | number;
  border_left_top_radius?: string | number;
  border_right_bottom_radius?: string | number;
  border_right_top_radius?: string | number;
}

interface BorderRadiusSelectorProps {
  name: string;
}

export const BorderRadiusSelector = ({ name }: BorderRadiusSelectorProps) => {
  const isPropertiesPanelV2 = useIsPropertiesPanelRevampEnabled();
  const { getValues, resetField } = useFormContext();
  const value = getValues(name) as BorderRadiusValues;

  const [selectedRadius, setSelectedRadius] =
    useState<ButtonRadiusSelectorType>("all");

  useEffect(() => {
    if (!value) return;

    const cornerValues = CORNERS.map(
      corner => value[corner as keyof BorderRadiusValues],
    );
    const first = cornerValues[0];
    const areAllEqual = cornerValues.every(val => val === first);

    setSelectedRadius(areAllEqual ? "all" : "individual");
  }, [value]);

  const handleRadiusChange = useCallback(
    (radius: ButtonRadiusSelectorType) => {
      setSelectedRadius(radius);

      if (radius === "all" && value) {
        const topLeft = value.border_left_top_radius;
        resetField(name, {
          defaultValue: {
            border_left_top_radius: topLeft,
            border_left_bottom_radius: topLeft,
            border_right_top_radius: topLeft,
            border_right_bottom_radius: topLeft,
          },
        });
      }
    },
    [name, value, resetField],
  );

  if (isPropertiesPanelV2) {
    return (
      <div className="flex flex-col gap-2">
        <div className="grid grid-cols-3 gap-2 items-center ">
          <Typography.LabelSm>
            <span className="text-dark text-xs font-normal">
              {t(
                "builder_ui.theme_configuration.site_styles.button_styles.corner_radius",
              )}
            </span>
          </Typography.LabelSm>
          <AllValuesInputField name={name} />
          <Selector
            selectedRadius={selectedRadius}
            setSelectedRadius={handleRadiusChange}
          />
        </div>
        <Inputs selectedRadius={selectedRadius} name={name} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 px-1">
      <div className="flex w-full items-center justify-between">
        <Typography.LabelSm>
          <span className="text-dark text-xs font-normal">
            {t(
              "builder_ui.theme_configuration.site_styles.button_styles.corner_radius",
            )}
          </span>
        </Typography.LabelSm>
        <Selector
          selectedRadius={selectedRadius}
          setSelectedRadius={handleRadiusChange}
        />
      </div>
      <Inputs selectedRadius={selectedRadius} name={name} />
    </div>
  );
};
